<template>
  <div v-if="isAuthenticated">
    <!-- Chat quando está aberto -->
    <transition name="chat-appear" appear>
      <div
        v-if="showChat"
        :class="['chat-floating', { minimized, fullscreen, docked, dragging: isDragging, 'dark-mode': isDarkMode }]"
        :style="floatingStyle"
        @mousedown.stop
        @touchstart.stop
      >
        <div
          class="chat-header"
          @mousedown.prevent="handleHeaderMouseDown"
          @touchstart.prevent="handleHeaderTouchStart"
        >
          <div class="title" v-if="!isEditingTitle">
            <v-icon small>mdi-chat-outline</v-icon>
            <span>{{ chatTitle }}</span>
          </div>
          <div class="title-edit" v-else @mousedown.stop @touchstart.stop>
            <v-text-field
              v-model="editingTitleValue"
              dense
              hide-details
              dark
              autofocus
              @keyup.enter="saveInlineTitle"
              @keyup.esc="cancelInlineTitle"
              class="title-input"
            ></v-text-field>
          </div>
          <div class="actions">
            <v-btn
              v-if="!minimized && conversationId && !isEditingTitle"
              icon
              x-small
              @click="startInlineEdit"
              :title="$t('renomearConversa')"
              @mousedown.stop
              @touchstart.stop
            >
              <v-icon x-small>mdi-pencil</v-icon>
            </v-btn>
            <v-btn
              v-if="!minimized && isEditingTitle"
              icon
              x-small
              @click="saveInlineTitle"
              color="success"
              :title="$t('salvar')"
              @mousedown.stop
              @touchstart.stop
            >
              <v-icon x-small>mdi-check</v-icon>
            </v-btn>
            <v-btn
              v-if="!minimized && isEditingTitle"
              icon
              x-small
              @click="cancelInlineTitle"
              color="error"
              :title="$t('cancelar')"
              @mousedown.stop
              @touchstart.stop
            >
              <v-icon x-small>mdi-close</v-icon>
            </v-btn>
            <v-btn
              v-if="!minimized && messages.length > 0"
              icon
              x-small
              @click="clearConversation"
              :title="$t('novaConversa')"
              @mousedown.stop
              @touchstart.stop
            >
              <v-icon x-small>mdi-plus</v-icon>
            </v-btn>
            <v-btn
              v-if="!minimized"
              icon
              x-small
              @click="toggleMinimize"
              :title="$t('minimizar')"
              @mousedown.stop
              @touchstart.stop
            >
              <v-icon x-small>mdi-minus</v-icon>
            </v-btn>
            <v-btn
              v-if="minimized"
              icon
              x-small
              @click="toggleMinimize"
              :title="$t('abrir')"
              @mousedown.stop
              @touchstart.stop
              class="minimize-btn-circle"
            >
              <v-icon x-small>mdi-chat-outline</v-icon>
            </v-btn>
            <v-btn
              v-if="!minimized"
              icon
              x-small
              @click="toggleFullscreen"
              :title="fullscreen ? $t('exitFullscreen') : $t('enterFullscreen')"
              @mousedown.stop
              @touchstart.stop
            >
              <v-icon x-small>{{ fullscreen ? 'mdi-close-box' : 'mdi-fullscreen' }}</v-icon>
            </v-btn>
            <v-btn
              v-if="!minimized"
              icon
              x-small
              @click="toggleDock"
              :title="docked ? $t('desencaixar') : $t('encaixarCanto')"
              @mousedown.stop
              @touchstart.stop
            >
              <v-icon x-small>{{ docked ? 'mdi-pin' : 'mdi-pin-off' }}</v-icon>
            </v-btn>
            <v-btn
              v-if="!minimized"
              icon
              x-small
              @click="toggleChat"
              :title="showChat ? $t('fechar') : $t('abrir')"
              @mousedown.stop
              @touchstart.stop
            >
              <v-icon x-small>mdi-close</v-icon>
            </v-btn>
          </div>
        </div>

        <transition name="expand">
          <div class="chat-body" v-show="!minimized">
            <div class="messages" ref="messagesContainer">
              <div v-for="(msg, index) in messages" :key="index" :class="['message', msg.sender]">
                <div v-if="msg.sender === 'ai'" class="ai-message-wrapper">
                  <div class="ai-message-content" v-html="renderMarkdown(msg.text)"></div>
                  <div v-if="msg.agent_id" class="floating-agent-label">
                    <v-icon x-small class="floating-agent-label__icon">mdi-robot-outline</v-icon>
                    <span>{{ formatAgentId(msg.agent_id) }}</span>
                  </div>
                  <div
                    v-if="ttsSupported || (chatService && msg.id)"
                    class="floating-message-actions"
                  >
                    <v-tooltip top v-if="ttsSupported">
                      <template v-slot:activator="{ on }">
                        <v-btn
                          x-small
                          icon
                          class="floating-action-btn"
                          :class="{ 'floating-action-btn--active': playingMessageIndex === index }"
                          @click="togglePlayMessage(index, msg.text)"
                          v-on="on"
                          :aria-label="playingMessageIndex === index ? 'Parar' : 'Ouvir resposta'"
                        >
                          <v-icon x-small>
                            {{ playingMessageIndex === index ? 'mdi-stop-circle' : 'mdi-volume-high' }}
                          </v-icon>
                        </v-btn>
                      </template>
                      <span>{{ playingMessageIndex === index ? 'Parar' : 'Ouvir resposta' }}</span>
                    </v-tooltip>

                    <span v-if="ttsSupported && chatService && msg.id" class="floating-actions-divider"></span>

                    <MessageFeedback
                      v-if="chatService && msg.id"
                      :message-id="String(msg.id)"
                      :initial-rating="msg.feedback || null"
                      :chat-service="chatService"
                      @feedback-submitted="onFeedbackSubmitted"
                      @feedback-error="onFeedbackError"
                    />
                  </div>
                </div>
                <div v-else>{{ msg.text }}</div>
              </div>
              <div v-if="messages.length === 0" class="empty-state">
                <div v-if="greetingMessage" class="greeting-container">
                  <div class="greeting-content " v-html="renderMarkdown(greetingMessage)"></div>
                </div>
                <span v-else>Olá! Como posso ajudar você?</span>
              </div>
            </div>
            <div class="input-row align-items-center">
              <v-text-field
                v-model="message"
                :placeholder="$t('digiteSuaMensagem')"
                outlined
                dense
                @keydown.enter="send"
                @keydown="onInputArrowKey"
                class="message-input"
                :disabled="floatingLoading"
              />
              <v-btn
                v-if="speechSupported"
                icon
                small
                :color="isRecording ? 'red' : 'primary'"
                :class="{ 'floating-mic-recording': isRecording }"
                @click="toggleRecording"
                :disabled="floatingLoading"
                :title="isRecording ? 'Parar gravação' : 'Gravar mensagem'"
                class="align-self-center"
              >
                <v-icon small>{{ isRecording ? 'mdi-microphone-off' : 'mdi-microphone' }}</v-icon>
              </v-btn>
              <v-btn
                icon
                small
                color="primary"
                @click="send"
                :disabled="!message.trim() || floatingLoading"
                :loading="floatingLoading"
                class="align-self-center"
              >
                <v-icon small v-if="!floatingLoading" class="align-self-center">mdi-send</v-icon>
              </v-btn>
            </div>
          </div>
        </transition>
      </div>
    </transition>

    <!-- Botão de reabertura quando o chat está fechado -->
    <transition name="button-appear">
      <div
        v-if="!showChat"
        class="chat-reopen-button"
        @click="toggleChat"
        :title="$t('abrirChatIA')"
      >
        <img src="@/assets/imgs/AI-Treasury/logo-AI-Treasury.png" alt="AI-Treasury" class="chat-reopen-logo" />
      </div>
    </transition>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { ChatService } from '@/views/modules/AI-Treasury/services/chatService';
import { MarkdownGenerator } from '@/views/modules/AI-Treasury/utils/markdownGenerator.js';
import audioService from '@/views/modules/AI-Treasury/utils/audioService.js';
import MessageFeedback from '@/views/modules/AI-Treasury/components/MessageFeedback.vue';

export default {
  name: 'ChatFloating',
  components: { MessageFeedback },
  data() {
    return {
      minimized: false,
      fullscreen: false,
      docked: true,
      showChat: false,
      message: '',
      posX: window.innerWidth - 420,
      posY: window.innerHeight - 500,
      isDragging: false,
      dragOffset: { x: 0, y: 0 },
      lastPosX: window.innerWidth - 420,
      lastPosY: window.innerHeight - 500,
      onDrag: null,
      endDrag: null,
      onWindowResize: null,
      isEditingTitle: false,
      editingTitleValue: '',
      greetingMessage: '',
      isRecording: false,
      speechSupported: false,
      ttsSupported: false,
      playingMessageIndex: null,
      sentViaAudio: false,
      finalizedText: '',
      chatService: null,
      inputHistory: [],
      inputHistoryIndex: -1,
      inputDraft: ''
    };
  },
  methods: {
    ...mapActions('chat', ['sendFloatingMessage', 'updateConversationTitle', 'clearFloatingMessages']),
    scrollToBottom() {
      if (this.$refs.messagesContainer) {
        this.$refs.messagesContainer.parentElement.scrollTop = this.$refs.messagesContainer.parentElement.scrollHeight;
      }
    },
    handleHeaderMouseDown(e) {
      if (!this.minimized) this.startDrag(e);
    },
    handleHeaderTouchStart(e) {
      if (!this.minimized) this.startDrag(e);
    },
    toggleMinimize() {
      this.minimized = !this.minimized;
      if (this.minimized) {
        if (this.fullscreen) {
          this.fullscreen = false;
        }
        this.docked = true;
        this.posX = null;
        this.posY = null;
        this.showChat = false;
      } else {
        this.showChat = true;
      }
    },
    toggleFullscreen() {
      this.fullscreen = !this.fullscreen;
      if (this.fullscreen) {
        this.docked = false;
        this.posX = null;
        this.posY = null;
      } else {
        this.docked = true;
        this.posX = null;
        this.posY = null;
      }
    },
    toggleDock() {
      this.docked = !this.docked;
      if (this.docked) {
        this.posX = null;
        this.posY = null;
      } else {
        if (this.posX == null || this.posY == null) {
          this.posX = window.innerWidth - 24 - 380;
          this.posY = window.innerHeight - 24 - 520;
        }
      }
    },
    toggleChat() {
      this.showChat = !this.showChat;
      if (this.showChat && this.minimized) {
        this.minimized = false;
      }
    },
    async send() {
      const text = this.message.trim();
      if (!text || this.floatingLoading) return;

      if (this.isRecording) {
        this.isRecording = false;
        audioService.stopRecognition();
      }

      if (this.inputHistory[this.inputHistory.length - 1] !== text) {
        this.inputHistory.push(text);
        if (this.inputHistory.length > 50) this.inputHistory.shift();
      }
      this.inputHistoryIndex = -1;
      this.inputDraft = '';

      const viaAudio = this.sentViaAudio;
      this.message = '';
      this.sentViaAudio = false;
      this.finalizedText = '';

      try {
        await this.sendFloatingMessage(text);
      } catch (error) {
        console.error('Erro ao enviar mensagem:', error);
      }
    },

    onInputArrowKey(event) {
      if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;

      if (event.key === 'ArrowUp') {
        if (this.inputHistory.length === 0) return;
        if (this.inputHistoryIndex === -1) {
          this.inputDraft = this.message;
          this.inputHistoryIndex = this.inputHistory.length - 1;
        } else if (this.inputHistoryIndex > 0) {
          this.inputHistoryIndex--;
        }
        this.message = this.inputHistory[this.inputHistoryIndex];
        event.preventDefault();
      } else {
        if (this.inputHistoryIndex === -1) return;
        if (this.inputHistoryIndex < this.inputHistory.length - 1) {
          this.inputHistoryIndex++;
          this.message = this.inputHistory[this.inputHistoryIndex];
        } else {
          this.inputHistoryIndex = -1;
          this.message = this.inputDraft;
          this.inputDraft = '';
        }
        event.preventDefault();
      }
    },
    startDrag(e) {
      if (this.fullscreen || this.docked || this.minimized) return;
      this.isDragging = true;
      const ev = e.touches ? e.touches[0] : e;
      this.dragOffset.x = ev.clientX;
      this.dragOffset.y = ev.clientY;
      this.lastPosX = this.posX;
      this.lastPosY = this.posY;
      document.addEventListener('mousemove', this.onDrag);
      document.addEventListener('mouseup', this.endDrag);
      document.addEventListener('touchmove', this.onDrag, { passive: false });
      document.addEventListener('touchend', this.endDrag);
    },
    // Converte markdown para HTML usando MarkdownGenerator (suporta tabelas)
    renderMarkdown(markdown) {
      if (!markdown) return '';
      return MarkdownGenerator.markdownToHtml(markdown);
    },
    startInlineEdit() {
      this.editingTitleValue = this.conversationTitle || 'AI-Treasury';
      this.isEditingTitle = true;
    },
    async saveInlineTitle() {
      if (!this.editingTitleValue.trim() || !this.conversationId) {
        this.cancelInlineTitle();
        return;
      }

      try {
        await this.updateConversationTitle({
          conversationId: this.conversationId,
          title: this.editingTitleValue.trim()
        });
        this.isEditingTitle = false;
      } catch (error) {
        alert('Erro ao renomear conversa. Tente novamente.');
      }
    },
    cancelInlineTitle() {
      this.isEditingTitle = false;
      this.editingTitleValue = '';
    },
    async clearConversation() {
      // Limpa o chat e cria uma nova conversa, mantendo a atual no histórico
      if (await this.$confirm(this.$t('novaConversaConfirmacao'))) {
        this.clearFloatingMessages();
      }
    },
    async loadGreetingMessage() {
      try {
        this.greetingMessage = await this.chatService.getGreetingMessage() || '';
      } catch (e) {
        this.greetingMessage = '';
      }
    },

    onFeedbackSubmitted({ messageId, rating }) {
      const msg = this.messages.find(m => String(m.id) === messageId);
      if (msg) this.$set(msg, 'feedback', rating);
    },

    onFeedbackError(err) {
      const errorMessage = (err && err.message) ? err.message : 'Falha ao registrar feedback';
      this.$alert(this.$t(errorMessage));
    },
    toggleRecording() {
      if (this.isRecording) {
        this.isRecording = false;
        audioService.stopRecognition();
        return;
      }
      this.isRecording = true;
      this.sentViaAudio = true;
      this.finalizedText = '';
      this._startListening();
    },
    _startListening() {
      audioService.startRecognition({
        lang: 'pt-BR',
        onResult: (transcript, isFinal) => {
          if (isFinal) {
            this.finalizedText += (this.finalizedText ? ' ' : '') + transcript;
            this.message = this.finalizedText;
          } else {
            this.message = this.finalizedText + (this.finalizedText ? ' ' : '') + transcript;
          }
        },
        onEnd: () => {
          if (this.isRecording) {
            this._startListening();
          }
        },
        onError: (event) => {
          if (event.error === 'no-speech' && this.isRecording) {
            this._startListening();
            return;
          }
          if (event.error !== 'aborted') {
            console.warn('Speech recognition error:', event.error);
          }
          this.isRecording = false;
        }
      });
    },
    formatAgentId(agentId) {
      if (!agentId) return '';
      return agentId
        .replace(/[-_]/g, ' ')
        .replace(/\b\w/g, c => c.toUpperCase());
    },

    togglePlayMessage(index, text) {
      if (this.playingMessageIndex === index) {
        audioService.stop();
        this.playingMessageIndex = null;
        return;
      }
      audioService.stop();
      this.playingMessageIndex = index;
      audioService.speak(text, {
        onEnd: () => { this.playingMessageIndex = null; },
        onError: () => { this.playingMessageIndex = null; }
      });
    },
  },
  computed: {
    ...mapState('chat', ['floatingMessages', 'floatingLoading', 'conversationTitle', 'conversationId']),
    isDarkMode() {
      return this.$vuetify.theme.dark;
    },
    messages() {
      return this.floatingMessages;
    },
    isAuthenticated() {
      const user = this.$store.state.auth?.user;
      return user && Object.keys(user).length > 0;
    },
    chatTitle() {
      // Se houver um título de conversa definido, usa ele
      if (this.conversationTitle) {
        return this.conversationTitle;
      }
      // Caso contrário, usa o título padrão
      return 'AI-Treasury';
    },
    floatingStyle() {
      if (this.fullscreen) {
        return { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, width: '100%', height: '100%' };
      }

      if (this.docked) {
        return {
          position: 'fixed',
          right: '24px',
          bottom: '24px',
          left: 'auto',
          top: 'auto'
        };
      } else {
        const width = this.minimized ? 64 : 380;
        const height = this.minimized ? 64 : 520;
        const maxX = Math.max(0, window.innerWidth - width);
        const maxY = Math.max(0, window.innerHeight - height);
        const translateX = Math.min(this.posX, maxX);
        const translateY = Math.min(this.posY, maxY);

        return {
          position: 'fixed',
          left: 0,
          top: 0,
          right: 'auto',
          bottom: 'auto',
          transform: `translate(${translateX}px, ${translateY}px)`
        };
      }
    },
  },
  created() {
    this.onDrag = (e) => {
      if (!this.isDragging) return;
      const ev = e.touches ? e.touches[0] : e;
      const deltaX = ev.clientX - this.dragOffset.x;
      const deltaY = ev.clientY - this.dragOffset.y;
      this.posX = Math.max(0, this.lastPosX + deltaX);
      this.posY = Math.max(0, this.lastPosY + deltaY);
      e.preventDefault && e.preventDefault();
    };
    this.endDrag = () => {
      this.isDragging = false;
      document.removeEventListener('mousemove', this.onDrag);
      document.removeEventListener('mouseup', this.endDrag);
      document.removeEventListener('touchmove', this.onDrag);
      document.removeEventListener('touchend', this.endDrag);
    };
    this.onWindowResize = () => {
      if (!this.docked && !this.fullscreen) {
        const chatWidth = this.minimized ? 64 : 380;
        const chatHeight = this.minimized ? 64 : 520;
        const maxX = Math.max(0, window.innerWidth - chatWidth);
        const maxY = Math.max(0, window.innerHeight - chatHeight);

        if (this.posX > maxX) {
          this.posX = maxX;
        }
        if (this.posY > maxY) {
          this.posY = maxY;
        }
      }
    };
  },
  watch: {
    isAuthenticated(val) {
      if (val && !this.greetingMessage) this.loadGreetingMessage();
    },
    messages: {
      handler(newMessages) {
        this.$nextTick(() => {
          this.scrollToBottom();
        });
        // Auto-play da resposta da IA quando enviada por áudio
        if (this.sentViaAudio && newMessages.length > 0) {
          const lastMsg = newMessages[newMessages.length - 1];
          if (lastMsg.sender === 'ai') {
            this.sentViaAudio = false;
            this.$nextTick(() => {
              this.togglePlayMessage(newMessages.length - 1, lastMsg.text);
            });
          }
        }
      },
      deep: true
    }
  },
  mounted() {
    window.addEventListener('resize', this.onWindowResize);
    window.addEventListener('orientationchange', this.onWindowResize);
    this.chatService = new ChatService();
    if (this.isAuthenticated) this.loadGreetingMessage();
    this.speechSupported = audioService.isSpeechRecognitionSupported();
    this.ttsSupported = audioService.isSpeechSynthesisSupported();
  },
  beforeDestroy() {
    if (this.endDrag) this.endDrag();
    window.removeEventListener('resize', this.onWindowResize);
    window.removeEventListener('orientationchange', this.onWindowResize);
    audioService.stop();
    if (this.isRecording) audioService.abortRecognition();
  },
};
</script>

<style scoped>
.chat-floating {
  z-index: 9999;
  position: fixed;
  width: 380px;
  height: 520px;
  max-width: calc(100% - 32px);
  box-shadow: 0 5px 40px rgba(0, 0, 0, 0.16), 0 3px 12px rgba(0, 0, 0, 0.12);
  border-radius: 12px;
  background: #ffffff;
  color: #333;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.chat-floating.dark-mode {
  background: #1e1e1e;
  color: #e0e0e0;
}

.chat-floating:not(.docked):not(.fullscreen) {
  cursor: grab;
}

.chat-floating.dragging {
  box-shadow: 0 8px 50px rgba(0, 0, 0, 0.25);
  cursor: grabbing !important;
  opacity: 0.96;
  transition-property: opacity, box-shadow;
}

.chat-floating.docked {
  right: 24px;
  bottom: 24px;
}

.chat-floating.minimized.docked {
  right: 24px !important;
  bottom: 24px !important;
}

.chat-floating.fullscreen {
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  width: 100% !important;
  height: 100% !important;
  border-radius: 0 !important;
  max-width: 100% !important;
}

.chat-floating.fullscreen.minimized {
  top: auto !important;
  left: auto !important;
  right: 24px !important;
  bottom: 24px !important;
  width: 64px !important;
  height: 64px !important;
  border-radius: 50% !important;
  max-width: 64px !important;
}

.chat-floating.minimized {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  padding: 0;
  min-width: 64px;
  min-height: 64px;
  max-width: 64px;
  max-height: 64px;
}

.chat-floating.minimized .chat-header {
  justify-content: center;
  align-items: center;
  padding: 0;
  height: 100%;
  width: 100%;
  border-radius: 50%;
  min-width: 64px;
  min-height: 64px;
}

.chat-floating.minimized .chat-header .title {
  display: none;
}

.chat-floating.minimized .chat-header .actions {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 64px;
  height: 64px;
  padding: 0;
  margin: 0;
}

.chat-floating.minimized .chat-header .actions .minimize-btn-circle {
  width: 64px !important;
  height: 64px !important;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 64px !important;
  min-height: 64px !important;
  padding: 0 !important;
}

.chat-floating.minimized .chat-header .actions .minimize-btn-circle ::v-deep .v-btn__content {
  display: flex;
  align-items: center;
  justify-content: center;
}

.chat-floating.minimized .chat-header .actions ::v-deep .v-btn {
  width: 64px !important;
  height: 64px !important;
  cursor: pointer;
  min-width: 64px !important;
  min-height: 64px !important;
  padding: 0 !important;
}

.chat-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  cursor: move;
  background: #66BB6A;
  color: white;
  flex-shrink: 0;
  border-radius: 12px 12px 0 0;
  font-weight: 600;
  user-select: none;
}

.chat-header .title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 14px;
  flex: 1;
  min-width: 0;
}

.chat-header .title span {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.chat-header .title-edit {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
}

.chat-header .title-edit .title-input {
  flex: 1;
}

.chat-header .title-edit ::v-deep .v-input__control {
  min-height: 32px !important;
}

.chat-header .title-edit ::v-deep .v-input__slot {
  padding: 0 8px !important;
  min-height: 32px !important;
  background-color: rgba(255, 255, 255, 0.2) !important;
  border-radius: 4px;
}

.chat-header .title-edit ::v-deep .v-text-field__slot input {
  color: white !important;
  font-size: 14px;
  font-weight: 600;
}

.chat-header .title-edit ::v-deep .v-text-field__slot input::placeholder {
  color: rgba(255, 255, 255, 0.7) !important;
}

.chat-header .title-edit ::v-deep fieldset {
  border-color: rgba(255, 255, 255, 0.3) !important;
}

.chat-header .title-edit ::v-deep .v-text-field--outlined.v-input--is-focused fieldset {
  border-color: rgba(255, 255, 255, 0.8) !important;
  border-width: 2px !important;
}

.chat-header .actions {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}

.chat-body {
  flex: 1;
  padding: 16px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-top: 1px solid #e0e0e0;
  scroll-behavior: smooth;
}

.chat-floating.dark-mode .chat-body {
  background: #2a2a2a;
  border-top: 1px solid #404040;
}

.messages {
  margin-bottom: 12px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.message {
  padding: 10px 12px;
  border-radius: 8px;
  word-wrap: break-word;
  max-width: 90%;
  font-size: 13px;
  line-height: 1.4;
  animation: messageSlideIn 0.3s ease-out;
}

@keyframes messageSlideIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.message.user {
  align-self: flex-end;
  background: #E3F2FD;
  color: #000000;
}

.chat-floating.dark-mode .message.user {
  background: #1565C0;
  color: #ffffff;
}

.message.ai {
  align-self: flex-start;
  background: #f5f5f5;
  color: #333;
}

.chat-floating.dark-mode .message.ai {
  background: #3a3a3a;
  color: #e0e0e0;
}

.empty-state {
  text-align: center;
  color: #999;
  font-size: 14px;
  margin: auto;
  width: 100%;
}

.empty-state--top {
  margin: 0;
  align-self: flex-start;
}

.greeting-container {
  text-align: left;
  width: 100%;
}

.greeting-content {
  font-size: 13px;
  line-height: 1.6;
  color: #555;
  white-space: normal;
}

.chat-floating.dark-mode .greeting-content {
  color: #b0b0b0;
}

.chat-floating.dark-mode .greeting-content ::v-deep h1,
.chat-floating.dark-mode .greeting-content ::v-deep h2,
.chat-floating.dark-mode .greeting-content ::v-deep h3 {
  color: #e0e0e0;
}

.greeting-content ::v-deep h1 {
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
}
.greeting-content ::v-deep h2 {
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
}

.chat-floating.dark-mode .empty-state {
  color: #666;
}

.input-row {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e0e0e0;
}

.input-row.align-items-center {
  align-items: center;
}

.input-row .v-btn.align-self-center {
  align-self: center;
  margin-bottom: 25px;
}

.input-row .v-icon.align-self-center {
  align-self: center;
}

.chat-floating.dark-mode .input-row {
  border-top: 1px solid #404040;
}

.input-row .message-input {
  flex: 1;
}

.input-row ::v-deep .v-text-field {
  flex: 1;
  padding: 0;
}

.chat-floating.dark-mode .input-row ::v-deep .v-text-field {
  flex: 1;
  padding: 0;
}

.chat-floating.dark-mode .input-row ::v-deep .v-text-field input {
  color: #e0e0e0 !important;
}

.chat-floating.dark-mode .input-row ::v-deep .v-text-field input::placeholder {
  color: #999 !important;
}

.chat-floating.dark-mode .input-row ::v-deep .v-label {
  color: #b0b0b0 !important;
}

.chat-floating.dark-mode .input-row ::v-deep .v-text-field .v-input__slot {
  background-color: #3a3a3a !important;
}

.chat-floating.dark-mode .input-row ::v-deep .v-text-field--outlined .v-input__slot {
  border: 1px solid #555 !important;
  background-color: #3a3a3a !important;
}

.chat-floating.dark-mode .input-row ::v-deep .v-text-field--outlined.v-input--is-focused .v-input__slot {
  border: 2px solid #667eea !important;
}

.chat-floating.dark-mode .input-row ::v-deep .v-field__outline__start {
  border-color: #555 !important;
}

.chat-floating.dark-mode .input-row ::v-deep .v-field__outline__end {
  border-color: #555 !important;
}

.chat-floating.dark-mode .input-row ::v-deep .v-field--focused .v-field__outline__start,
.chat-floating.dark-mode .input-row ::v-deep .v-field--focused .v-field__outline__end {
  border-color: #667eea !important;
}

.chat-floating.dark-mode .input-row ::v-deep .v-input {
  caret-color: #667eea;
}

.input-row ::v-deep .v-btn {
  flex-shrink: 0;
}

.chat-floating.dark-mode .input-row ::v-deep .v-btn {
  background-color: #667eea !important;
  color: white !important;
}

.chat-floating.dark-mode .input-row ::v-deep .v-btn:hover:not(:disabled) {
  background-color: #7a8eed !important;
}

.chat-floating.dark-mode .input-row ::v-deep .v-btn:disabled {
  background-color: #555 !important;
  color: #999 !important;
}

.ai-message-wrapper {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.ai-message-content {
  white-space: pre-line;
  word-wrap: break-word;
  overflow-wrap: break-word;
  width: 100%;
}

.ai-message-content h1 {
  font-size: 18px;
  font-weight: 700;
  margin: 8px 0;
  line-height: 1.3;
}

.ai-message-content h2 {
  font-size: 16px;
  font-weight: 700;
  margin: 8px 0;
  line-height: 1.3;
}

.ai-message-content h3 {
  font-size: 14px;
  font-weight: 700;
  margin: 8px 0;
  line-height: 1.3;
}

.ai-message-content strong {
  font-weight: 700;
}

.ai-message-content em {
  font-style: italic;
}

.ai-message-content a {
  color: #667eea;
  text-decoration: none;
  cursor: pointer;
  border-bottom: 1px solid #667eea;
}

.ai-message-content a:hover {
  opacity: 0.8;
}

.chat-floating.dark-mode .ai-message-content a {
  color: #8a9eff;
  border-bottom-color: #8a9eff;
}

.ai-message-content ::v-deep p {
  margin-bottom: 8px;
}

.ai-message-content ::v-deep p:last-child {
  margin-bottom: 0;
}

.ai-message-content ::v-deep ul,
.ai-message-content ::v-deep ol {
  margin: 8px 0;
  padding-left: 18px;
  list-style: disc;
}

.ai-message-content ::v-deep li {
  margin: 4px 0;
  line-height: 1.5;
  font-size: 13px;
  word-break: break-word;
  overflow-wrap: break-word;
}

.ai-message-content .list-item {
  margin: 4px 0;
  line-height: 1.5;
  font-size: 13px;
  word-break: break-word;
  overflow-wrap: break-word;
  display: flex;
  gap: 6px;
  align-items: flex-start;
}

.ai-message-content .list-item::before {
  content: '';
  flex-shrink: 0;
}

/* Wrapper para tabelas com scroll horizontal */
.ai-message-content ::v-deep .markdown-table-wrapper,
.greeting-content ::v-deep .markdown-table-wrapper {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  border-radius: 8px;
  margin: 12px 0;
  max-width: 100%;
  display: block;
}

/* Estilos para tabelas em markdown */
.ai-message-content ::v-deep table.markdown-table,
.greeting-content ::v-deep table.markdown-table {
  width: 100%;
  border-collapse: collapse;
  margin: 0;
  border: 1px solid #e0e0e0;
  overflow: hidden;
  font-size: 13px;
  background-color: white;
  min-width: 100%;
}

.chat-floating.dark-mode .ai-message-content ::v-deep .markdown-table-wrapper,
.chat-floating.dark-mode .greeting-content ::v-deep .markdown-table-wrapper {
  border-radius: 8px;
}

.chat-floating.dark-mode .ai-message-content ::v-deep table.markdown-table,
.chat-floating.dark-mode .greeting-content ::v-deep table.markdown-table {
  background-color: #2a2a2a;
  border-color: #444444;
}

.ai-message-content ::v-deep table.markdown-table thead,
.greeting-content ::v-deep table.markdown-table thead {
  background-color: #f8f9fa;
  border-bottom: 2px solid #e0e0e0;
}

.chat-floating.dark-mode .ai-message-content ::v-deep table.markdown-table thead,
.chat-floating.dark-mode .greeting-content ::v-deep table.markdown-table thead {
  background-color: #2d2d2d;
  border-bottom-color: #444444;
}

.ai-message-content ::v-deep table.markdown-table th,
.greeting-content ::v-deep table.markdown-table th {
  padding: 10px 14px;
  text-align: right;
  font-weight: 600;
  color: #1e293b;
  white-space: nowrap;
}

.chat-floating.dark-mode .ai-message-content ::v-deep table.markdown-table th,
.chat-floating.dark-mode .greeting-content ::v-deep table.markdown-table th {
  color: #ffffff;
}

.ai-message-content ::v-deep table.markdown-table td,
.greeting-content ::v-deep table.markdown-table td {
  padding: 8px 14px;
  border-bottom: 1px solid #f0f0f0;
  text-align: right;
}

.chat-floating.dark-mode .ai-message-content ::v-deep table.markdown-table td,
.chat-floating.dark-mode .greeting-content ::v-deep table.markdown-table td {
  border-bottom-color: #333333;
}

.ai-message-content ::v-deep table.markdown-table tr:last-child td,
.greeting-content ::v-deep table.markdown-table tr:last-child td {
  border-bottom: none;
}

.ai-message-content ::v-deep table.markdown-table tbody tr:hover,
.greeting-content ::v-deep table.markdown-table tbody tr:hover {
  background-color: #f5f5f5;
}

.chat-floating.dark-mode .ai-message-content ::v-deep table.markdown-table tbody tr:hover,
.chat-floating.dark-mode .greeting-content ::v-deep table.markdown-table tbody tr:hover {
  background-color: #2d2d2d;
}

/* Alinha primeira coluna à esquerda */
.ai-message-content ::v-deep table.markdown-table td:first-child,
.ai-message-content ::v-deep table.markdown-table th:first-child,
.greeting-content ::v-deep table.markdown-table td:first-child,
.greeting-content ::v-deep table.markdown-table th:first-child {
  text-align: left;
}

/* Transitions */
.chat-appear-enter-active {
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

.chat-appear-enter {
  opacity: 0;
  transform: scale(0.3) translate(280px, 390px);
}

.chat-appear-enter-to {
  opacity: 1;
  transform: scale(1) translate(0, 0);
}

.button-appear-enter-active,
.button-appear-leave-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.button-appear-enter {
  opacity: 0;
  transform: scale(0);
}

.button-appear-enter-to {
  opacity: 1;
  transform: scale(1);
}

.button-appear-leave {
  opacity: 1;
  transform: scale(1);
}

.button-appear-leave-to {
  opacity: 0;
  transform: scale(0);
}

.expand-enter-active,
.expand-leave-active {
  transition: all 0.2s ease;
}

.expand-enter,
.expand-leave-to {
  transform: scaleY(0.9);
  opacity: 0;
}

/* Scrollbar styling */
.chat-body::-webkit-scrollbar {
  width: 6px;
}

.chat-body::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.chat-body::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 10px;
}

.chat-body::-webkit-scrollbar-thumb:hover {
  background: #999;
}

.chat-floating.dark-mode .chat-body::-webkit-scrollbar-track {
  background: #3a3a3a;
}

.chat-floating.dark-mode .chat-body::-webkit-scrollbar-thumb {
  background: #555;
}

.chat-floating.dark-mode .chat-body::-webkit-scrollbar-thumb:hover {
  background: #777;
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .chat-floating {
    width: calc(100vw - 48px) !important;
    max-width: 100% !important;
  }

  .chat-body {
    max-height: calc(100vh - 200px);
  }

  .chat-reopen-button {
    bottom: 24px !important;
    right: 24px !important;
  }
}

/* Extra small devices */
@media (max-width: 480px) {
  .chat-floating {
    width: calc(100vw - 32px) !important;
  }

  .chat-floating.minimized {
    width: 56px !important;
    height: 56px !important;
  }

  .chat-floating.minimized .chat-header .actions ::v-deep .v-btn {
    width: 56px !important;
    height: 56px !important;
    min-width: 56px !important;
    min-height: 56px !important;
  }
}

/* Botão de reabertura do chat */
.chat-reopen-button {
  position: fixed;
  bottom: 78px;
  right: 24px;
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 9999;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  user-select: none;
}

.chat-reopen-button:hover {
  transform: scale(1.1);
}

.chat-reopen-button:active {
  transform: scale(0.95);
}

.chat-reopen-logo {
  width: 84px;
  height: 64px;
  object-fit: contain;
  pointer-events: none;
  filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.2));
}

::v-deep .v-overlay {
  z-index: 9999 !important;
}

/* Label do agente no chat flutuante */
.floating-agent-label {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-top: 6px;
  padding: 2px 6px;
  border-radius: 10px;
  background-color: #f0f4ff;
  border: 1px solid #d0d9f7;
  font-size: 10px;
  color: #5c6bc0;
  font-weight: 500;
  line-height: 1.4;
  width: fit-content;
}

.chat-floating.dark-mode .floating-agent-label {
  background-color: #1a237e22;
  border-color: #3949ab55;
  color: #9fa8da;
}

.floating-agent-label__icon {
  color: #5c6bc0 !important;
  font-size: 11px !important;
}

.chat-floating.dark-mode .floating-agent-label__icon {
  color: #9fa8da !important;
}

/* Barra de ações da mensagem IA no chat flutuante */
.floating-message-actions {
  display: flex;
  align-items: center;
  gap: 2px;
  margin-top: 4px;
  opacity: 0;
  transition: opacity 0.15s ease;
}

.message.ai:hover .floating-message-actions {
  opacity: 1;
}

.floating-message-actions:has(.floating-action-btn--active) {
  opacity: 1;
}

.floating-action-btn {
  color: #757575 !important;
  border-radius: 6px !important;
}

.floating-action-btn:hover {
  background-color: rgba(0, 0, 0, 0.06) !important;
}

.floating-action-btn--active {
  color: #1976d2 !important;
}

.dark-mode .floating-action-btn {
  color: #9e9e9e !important;
}

.dark-mode .floating-action-btn:hover {
  background-color: rgba(255, 255, 255, 0.08) !important;
}

.dark-mode .floating-action-btn--active {
  color: #64b5f6 !important;
}

.floating-actions-divider {
  display: inline-block;
  width: 1px;
  height: 12px;
  background-color: #e0e0e0;
  margin: 0 3px;
  flex-shrink: 0;
}

.dark-mode .floating-actions-divider {
  background-color: #444;
}

/* Animação pulsante para gravação no chat flutuante */
.floating-mic-recording {
  animation: floating-pulse-recording 1.5s ease-in-out infinite;
}

@keyframes floating-pulse-recording {
  0%, 100% { box-shadow: 0 0 0 0 rgba(244, 67, 54, 0.4); }
  50% { box-shadow: 0 0 0 6px rgba(244, 67, 54, 0); }
}
</style>
